import {
  PrebidChildSegmentData,
  PrebidProviderData,
  PrebidNormalizedSegmentData,
  PrebidSearchSegmentsData,
} from '../../models/PreBidAudience'
import { RowSelectionState } from '@tanstack/react-table'

// 11,13,14 creative ids depecits all creative segment
const creativeAllType = '11,13,14'

export const transformInitDataToRowSelection = (
  initialData?: Record<string, PrebidNormalizedSegmentData>
) => {
  const result: RowSelectionState = {}
  for (const [key] of Object.entries(initialData ?? {})) {
    result[key] = true
  }
  return result
}

export const transformParentProviderData = (data: PrebidProviderData[]) => {
  if (!Array.isArray(data)) {
    return [] as PrebidNormalizedSegmentData[]
  }
  return data.map(one => {
    return {
      value: `provider-${one.providerId}`,
      preservedValue: `${one.providerId}`,
      isProvider: true,
      label: one.providerName,
      description: one.description,
      providerId: one.providerId,
      providerName: one.providerName,
      providerLogoUrl: one.providerLogoUrl,
      parentPathKey: null,
      cpm: 0,
      isLeaf: false,
      creativeTypeIdList: [],
      creativeTypeIdSerialized: '',
      isAllCreativeType: false,
      subRows: transformChildSegmentData(one.childSegments, one),
    } as PrebidNormalizedSegmentData
  })
}

export const transformChildSegmentData = (
  data: PrebidChildSegmentData[],
  providerData: PrebidProviderData
) => {
  if (!Array.isArray(data)) {
    return [] as PrebidNormalizedSegmentData[]
  }
  return data.map(one => {
    const serializedCreativeTypeIds = (one.creativeTypeIdList ?? [])
      .sort()
      .join(',')

    return {
      value: one.isLeaf
        ? `${one.prebidAudienceSegmentId}`
        : `intermediate-segment-${one.prebidAudienceSegmentId}`,
      preservedValue: `${one.prebidAudienceSegmentId}`,
      isProvider: false,
      label: one.prebidAudienceSegmentName,
      description: one.description,
      parentPathKey: one.parentPathKey,
      cpm: one.cpm,
      creativeTypeIdList: one.creativeTypeIdList ?? [],
      creativeTypeIdSerialized: serializedCreativeTypeIds,
      isAllCreativeType: serializedCreativeTypeIds === creativeAllType,
      isLeaf: one.isLeaf,
      subRows: [],
      providerId: providerData.providerId,
      providerName: providerData.providerName,
      providerLogoUrl: providerData.providerLogoUrl,
    } as PrebidNormalizedSegmentData
  })
}

export const transformSearchSegmentData = (
  data: PrebidSearchSegmentsData[],
  providersData: Record<string, Omit<PrebidProviderData, 'childSegments'>> = {}
) => {
  if (!Array.isArray(data)) {
    return [] as PrebidNormalizedSegmentData[]
  }
  const result = data.map(one => {
    const serializedCreativeTypeIds = (one.creativeTypeIdList ?? [])
      .sort()
      .join(',')
    return {
      value: `${one.prebidAudienceSegmentId}`,
      preservedValue: `${one.prebidAudienceSegmentId}`,
      isProvider: false,
      label: one.prebidAudienceSegmentName,
      description: one.description,
      parentPathKey: one.parentPathKey,
      cpm: one.cpm,
      creativeTypeIdList: one.creativeTypeIdList ?? [],
      creativeTypeIdSerialized: serializedCreativeTypeIds,
      isAllCreativeType: serializedCreativeTypeIds === creativeAllType,
      isLeaf: one.isLeaf,
      subRows: [],
      providerId: one.providerId,
      providerName: providersData?.[one.providerId]?.providerName ?? '',
      providerLogoUrl: providersData?.[one.providerId]?.providerLogoUrl ?? '',
    } as PrebidNormalizedSegmentData
  })
  return result
}

export const constructProviderDataFromSearchResults = (
  searchResults: PrebidSearchSegmentsData[],
  providersData: Record<string, Omit<PrebidProviderData, 'childSegments'>> = {}
) => {
  const transformedSearchResult = transformSearchSegmentData(
    searchResults,
    providersData
  )

  const providerWiseResult = transformedSearchResult.reduce(
    (prev, current) => {
      if (Array.isArray(prev[current.providerId])) {
        const providerWisePrebidData = prev[current.providerId]!
        providerWisePrebidData.push(current)
        return { ...prev, [current.providerId]: providerWisePrebidData }
      }
      const providerWisePrebidData = [current]
      return { ...prev, [current.providerId]: providerWisePrebidData }
    },
    {} as Record<string, PrebidNormalizedSegmentData[]>
  )

  const allProviders: PrebidNormalizedSegmentData[] = []

  for (const [provider, providerSegmenetData] of Object.entries(
    providerWiseResult
  )) {
    const transformedProviderData = transformParentProviderData([
      providersData[provider] as PrebidProviderData,
    ])
    providerSegmenetData.forEach(segment => {
      insertSegmentAtPath(
        transformedProviderData,
        Number(provider),
        segment.parentPathKey!,
        segment
      )
    })

    const result = transformedProviderData[0]
    allProviders.push(result!)
  }

  return allProviders
}

const constructIntermediateSegmentFromLabel = (
  label: string,
  providerData: PrebidNormalizedSegmentData,
  parentPath: string[]
): PrebidNormalizedSegmentData => {
  const valueStr = [providerData.providerName, ...parentPath, label].join('.')
  return {
    label: label,
    description: '',
    parentPathKey: parentPath.join(' > '),
    creativeTypeIdList: [],
    creativeTypeIdSerialized: '',
    isAllCreativeType: false,
    isLeaf: false,
    cpm: 0,
    value: valueStr,
    preservedValue: valueStr,
    isProvider: false,
    providerId: providerData.providerId,
    providerName: providerData.providerName ?? '',
    providerLogoUrl: providerData.providerLogoUrl ?? '',
    subRows: [],
  }
}

const insertSegmentAtPath = (
  originalObject: PrebidNormalizedSegmentData[],
  providerId: number,
  pathKey: string,
  segment: PrebidNormalizedSegmentData
) => {
  const pathKeyArray = pathKey.split('>').map(t => t.trim())
  if (pathKeyArray.length <= 0) {
    return originalObject
  }
  const objectToInsertInto = originalObject.filter(
    one => one.providerId === providerId
  )[0]
  if (!objectToInsertInto) {
    return originalObject
  }

  // get the segments and start nested insertion at provided path
  return setInAtPath(
    objectToInsertInto.subRows,
    pathKeyArray.slice(),
    segment,
    objectToInsertInto, // provider data
    []
  )
}

const setInAtPath = (
  originalObject: PrebidNormalizedSegmentData[],
  pathKey: string[],
  segment: PrebidNormalizedSegmentData,
  providerData: PrebidNormalizedSegmentData,
  parentPath: string[]
) => {
  if (pathKey.length > 0) {
    const currentPath = pathKey.splice(0, 1)[0]
    const nextParentPath = parentPath.slice()
    nextParentPath.push(currentPath as string)

    const insertAtNode = originalObject.find(one => one.label === currentPath)

    if (insertAtNode) {
      setInAtPath(
        insertAtNode.subRows,
        pathKey,
        segment,
        providerData,
        nextParentPath
      )
    } else {
      const node = constructIntermediateSegmentFromLabel(
        currentPath!,
        providerData,
        parentPath
      )
      originalObject.push(node)
      setInAtPath(node.subRows, pathKey, segment, providerData, nextParentPath)
    }
  } else {
    originalObject.push(segment)
  }
}
